import {
  Button,
  Classes,
  Colors,
  Dialog,
  FormGroup,
  Icon,
  InputGroup,
  Intent,
  Radio,
  RadioGroup,
  TextArea
} from '@blueprintjs/core'
import { IconNames } from '@blueprintjs/icons'
import * as React from 'react'
import Linkify from 'react-linkify'
import client from '../../../client'
import Errors from '../../../components/Errors'
import { UprightStyle } from '../../../types'
import { PreferredItemTag } from './PreferredItemTag'
import { ManifestPreferredItem } from '../../../types/api'

interface Props {
  isOpen: boolean
  onClose: () => void
  onDelete?: () => void
  onSaveComplete: () => void
  preferredItem?: ManifestPreferredItem
  readonly?: boolean
}

export interface State {
  loading: boolean
  preview: boolean
  errors: any
  preferredItem: ManifestPreferredItem
}

const styles: UprightStyle = {
  noMarginButton: {
    marginLeft: 0
  },
  buttonToolbar: {
    display: 'flex',
    justifyContent: 'space-between',
    width: '100%'
  },
  textArea: {
    minHeight: 124
  },
  readonlyDescription: { paddingLeft: 16, borderLeft: `4px solid ${Colors.GRAY3}`, whiteSpace: 'pre-wrap' },
  itemTag: { marginBottom: 20 },
  previewButtonBar: { display: 'flex', alignItems: 'center', gap: 15 },
  editButtonBar: { display: 'flex', alignItems: 'center', gap: 5 },
  backBtn: { cursor: 'pointer', color: Colors.BLUE3 },
  dismissBtn: { cursor: 'pointer', color: Colors.GRAY3 }
}

const BLANK_ITEM: ManifestPreferredItem = {
  id: null,
  title: '',
  description: '',
  preference: 'PREFERRED'
}

export class ManifestPreferredItemDialog extends React.Component<Props, State> {
  constructor(props) {
    super(props)
    this.state = {
      loading: false,
      preview: !!props.readonly,
      errors: null,
      preferredItem: props.preferredItem || BLANK_ITEM
    }
  }

  onClose = () => {
    const { onClose, readonly } = this.props
    onClose()

    this.setState({
      loading: false,
      preview: !!readonly,
      errors: null,
      preferredItem: BLANK_ITEM
    })
  }

  loadPreferredItem = () => {
    const { preferredItem } = this.props
    this.setState({ preferredItem: preferredItem || BLANK_ITEM })
  }

  onSubmit = async () => {
    try {
      const payload = { manifestPreferredItem: this.state.preferredItem }
      if (this.state.preferredItem.id) {
        await client.updateManifestPreferredItem(this.state.preferredItem.id, payload)
        analytics.track(`Manifest Preferred Item Created`)
      } else {
        await client.createManifestPreferredItem(payload)
        analytics.track(`Manifest Preferred Item Updated`)
      }
      this.onClose()
      this.props.onSaveComplete()
    } catch (e) {
      // eslint-disable-next-line no-console
      console.log(e)
      this.setState({ errors: e.response.data, loading: false })
    }
  }

  preferredItemChange = (field, value) => {
    this.setState(prevState => ({ preferredItem: { ...prevState.preferredItem, [field]: value } }))
  }

  dialogTitle = () => {
    const { preferredItem, preview } = this.state
    if (preview) return preferredItem.title
    if (preferredItem.id) return 'Edit item preference'
    return 'Add item preference'
  }

  onEnablePreview = () => this.setState({ preview: true })

  onDisablePreview = () => this.setState({ preview: false })

  render() {
    const { errors, loading, preferredItem, preview } = this.state
    const { readonly } = this.props

    const saveDisabled = preferredItem.preference === null
    return (
      <Dialog
        onClose={this.onClose}
        title={this.dialogTitle()}
        portalClassName="upright-popover-safe-dialog"
        isOpen={this.props.isOpen}
        onOpening={this.loadPreferredItem}
      >
        <div className={Classes.DIALOG_BODY}>
          <Errors error={errors} />
          {!preview && (
            <FormGroup label="Title">
              <InputGroup
                value={preferredItem.title}
                placeholder="Add a title..."
                onChange={v => this.preferredItemChange('title', v.target.value)}
              />
            </FormGroup>
          )}
          {preview && (
            <div style={styles.itemTag}>
              <PreferredItemTag large preference={preferredItem.preference} />
            </div>
          )}
          {preview && preferredItem.description && (
            <div style={styles.readonlyDescription}>
              <Linkify>{preferredItem.description}</Linkify>
            </div>
          )}
          {preview && !preferredItem.description && <div className={Classes.TEXT_MUTED}>No details provided.</div>}
          {!preview && (
            <FormGroup label="Description">
              <TextArea
                fill
                value={preferredItem.description}
                placeholder={`Add a description of the preference...

Include live listing examples to help guide stores:
- Great example: https://ebay.com/itm/2019561246
- This kind will not sell: http://shopgoodwill.com/item/6421659102
`}
                onChange={v => this.preferredItemChange('description', v.target.value)}
                style={styles.textArea}
              />
            </FormGroup>
          )}
          {!preview && (
            <FormGroup label="Preference">
              <RadioGroup
                selectedValue={preferredItem.preference}
                onChange={v => this.preferredItemChange('preference', v.currentTarget.value)}
                inline
              >
                <Radio label="Send" value="PREFERRED" />
                <Radio label="Don't send" value="NOT_PREFERRED" />
              </RadioGroup>
            </FormGroup>
          )}
        </div>

        <div className={Classes.DIALOG_FOOTER}>
          <div className={Classes.DIALOG_FOOTER_ACTIONS} style={styles.buttonToolbar}>
            {preview && (
              <div style={styles.previewButtonBar}>
                {!readonly && (
                  <div style={styles.backBtn} onClick={this.onDisablePreview}>
                    <Icon icon={IconNames.CHEVRON_LEFT} /> Back
                  </div>
                )}
                {readonly && (
                  <div style={styles.dismissBtn} onClick={this.onClose}>
                    Dismiss
                  </div>
                )}
              </div>
            )}
            {!preview && (
              <>
                <div style={styles.editButtonBar}>
                  <Button
                    style={styles.noMarginButton}
                    intent={Intent.SUCCESS}
                    onClick={this.onSubmit}
                    loading={loading}
                    disabled={saveDisabled}
                    text="Save"
                  />
                  <Button
                    style={styles.noMarginButton}
                    onClick={this.onEnablePreview}
                    intent={Intent.PRIMARY}
                    minimal
                    text="Preview"
                  />
                  <Button style={styles.noMarginButton} onClick={this.onClose} minimal text="Cancel" />
                </div>
              </>
            )}
            {!preview && preferredItem.id && this.props.onDelete && (
              <Button
                icon={IconNames.TRASH}
                text="Delete"
                intent={Intent.DANGER}
                minimal
                onClick={this.props.onDelete}
              />
            )}
          </div>
        </div>
      </Dialog>
    )
  }
}
